import Switch from "@imminently/decisively-ui/Switch";
import { usePreferences } from "../PreferencesContext";
import type { FeatureSettings } from "@/config/features";

type LegacyPagesKey = Exclude<keyof FeatureSettings["legacy_pages"], "enabled">;

const LEGACY_PAGE_FEATURES: Array<{
  key: LegacyPagesKey;
  label: string;
  description: string;
}> = [
  {
    key: "conversations",
    label: "Conversations",
    description: "Show the legacy conversation designer",
  },
  {
    key: "reports",
    label: "Reports",
    description: "Show the legacy reports designer",
  },
  {
    key: "bots",
    label: "Bots",
    description: "Show the legacy bots page",
  },
  {
    key: "reporting",
    label: "Reporting",
    description: "Show the legacy reporting configuration page",
  },
  {
    key: "graph_testing",
    label: "Graph Testing",
    description: "Show the graph testing tab in Verify",
  },
  {
    key: "interactive_reports",
    label: "Interactive Reports",
    description: "Show the interactive tab in decision reports",
  },
];

export const LegacyPagesPanel = () => {
  const { featureSettings, setLegacyPagesEnabled, toggleFeature } = usePreferences();
  const enabled = featureSettings.legacy_pages.enabled;

  return (
    <div className="p-8 space-y-6 max-w-2xl">
      <div>
        <h2 className="text-2xl font-bold">Legacy Pages</h2>
        <p className="text-muted-foreground mt-1">Control visibility to legacy features. These are currently unsupported and may not fully function.</p>
      </div>

      <div className="space-y-2">
        <div className="flex items-start justify-between gap-4 px-4 py-4 rounded-lg border bg-card">
          <div className="flex-1 min-w-0">
            <label
              htmlFor="legacy_pages.enabled"
              className="block text-sm font-medium cursor-pointer mb-1"
            >
              Legacy Pages
            </label>
            <p className="text-sm text-muted-foreground">Toggle all legacy page entries together</p>
          </div>
          <Switch
            id="legacy_pages.enabled"
            checked={enabled}
            onCheckedChange={(checked) => setLegacyPagesEnabled(Boolean(checked))}
          />
        </div>

        {LEGACY_PAGE_FEATURES.map((feature) => (
          <div
            key={feature.key}
            className={`ml-6 flex items-start justify-between gap-4 px-4 py-4 rounded-lg border bg-card ${enabled ? "opacity-75" : ""}`}
          >
            <div className="flex-1 min-w-0">
              <label
                htmlFor={`legacy_pages.${feature.key}`}
                className="block text-sm font-medium mb-1"
              >
                {feature.label}
              </label>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
            </div>
            <Switch
              id={`legacy_pages.${feature.key}`}
              checked={enabled || featureSettings.legacy_pages[feature.key]}
              disabled={enabled}
              onCheckedChange={() => toggleFeature(`legacy_pages.${feature.key}`)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
